import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { myUserId } from '../../../user/user.selector';
import {
  AnimationKeys,
  CommentShapeDescriptor,
  Comment,
} from '../../../elements/resource/types/shape.type';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  _setRelationshipBase,
  addNewShapeAction,
  appendComment,
  clearCommentDraft,
  deleteShapeFailure,
  generateImage,
  generateImageSuccess,
  loadProjectPreview,
  loadProjectPreviewRequest,
  openCanvasBgColorEdit,
  setAnimationConfig,
  setAnimationConfigBase,
  setChangeTookPlace,
  setProjectInput,
  setProjectPreviewIsLoading,
  setRelationships,
  setResourceLabel,
  setResourceLabelBase,
  setSelectedShapes,
  toggleEditorState,
} from '../editor.actions';
import {
  addShapeToStore,
  setOriginalDescriptor,
  saveChangedShapes,
  setShapeOriginalTransform,
  setOriginalSVGAttributes,
  openFile,
  addFileToStore,
  setFileLoading,
  loadFileAction,
  setCanvasShapes,
  setRelationship,
  setRelationshipBase,
  incrementRadius,
  setShape,
  bulkUpdateFinish,
  setBulkUpdateItem,
  setResourcePatch,
  createNewComponentAction,
  saveResourcesAction,
  setResourcePost,
  setResourceDelete,
  removeRelationshipBase,
  deselectShape,
  addFile,
} from '../editor.actions';
import {
  setDescriptorValue,
  incrementStrokeWidth,
  deleteShapeAction,
} from '../editor.crud.actions';
import {
  selectedShapes,
  selectNoAnimationMode,
  selectedSelectedState,
  selectShapes,
  selectDescriptors,
  currentDescriptors,
  selectedShapeList,
  currentSVGAttributes,
  selectShapeTransforms,
  baseSvgAttributes,
  selectUpdatesByState,
  selectCrudEvents,
  _selectedShapes,
  selectCurrentAnimationId,
  selectAnimationsByResource,
  selectResources,
  selectSelectedShapes,
  selectCurrentFileIRI,
  selectFiles as selectEditorFiles,
  selectProjects as selectEditorProjects,
} from '../selector/editor.selector';
import {
  CircleShapeDescriptor,
  GeneralShapeDescriptor,
  RectangleShapeDescriptor,
  SVGAttributes,
  Translate,
} from '../../../elements/resource/types/shape.type';
import { pick as _pick, cloneDeep, get, pick } from 'lodash';
import {
  removeAnimationItemAction,
  removeAnimationItemBaseAction,
  setAnimationItemAction,
  setCurrentAnimationId,
  setMainAnimationFrame,
} from '../../animation/store/animation.actions';
import { selectCurrentlyAddedShapes } from '../../animation/store/animation.selector';
import { IdToIRI, IRIToId } from '../../../util/iri';
import { ResourcePatch } from '../reducer/editor.reducer';
import { ResourceData } from '../../../elements/resource/resource.types';
import {
  selectAllProjects,
  selectCurrentFileID,
  selectFiles,
} from '../../../projects/project.selector';
import {
  addShapeToFile,
  setCurrentFileIRI,
} from '../../../projects/project.actions';
import { EditorService } from '../../editor.service';

@Injectable()
export class EditorEffects {
  generateImage$: Observable<Action>;
  setDescriptorValue$: Observable<Action>;
  loadProjectPreview$: Observable<Action>;
  loadProjectPreviewRequest$: Observable<Action>;
  addShapeToStore$: Observable<Action>;
  deleteShape$: Observable<Action>;
  newShape$: Observable<Action>;
  setShapeAttributeAction$: Observable<Action>;
  updateShapeDescriptorAction$: Observable<Action>;
  removeAttribute$: Observable<Action>;
  resetBaseState$: Observable<Action>;
  selectSubScene$: Observable<Action>;
  addNewScene$: Observable<Action>;
  switchScene$: Observable<Action>;
  incrementRadius$: Observable<Action>;
  incrementStrokeWidth$: Observable<Action>;
  openFile$: Observable<Action>;
  addFileToStore$: Observable<Action>;
  setRelationship$: Observable<Action>;
  createNewComponent$: Observable<Action>;
  saveResources$: Observable<Action>;
  setStoryBoardItem$: Observable<Action>;
  appendComment$: Observable<Action>;
  addScene$: Observable<Action>;
  removeAnimationItem$: Observable<Action>;
  setResourceLabel$: Observable<Action>;
  setMainAnimationFrame$: Observable<Action>;
  openCanvasBgColorEdit$: Observable<Action>;
  setAnimationConfig$: Observable<Action>;

  setRelationships$: Observable<Action>;
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly editorService: EditorService,
  ) {
    this.loadProjectPreview$ = createEffect(() =>
      this.actions$.pipe(
        ofType(loadProjectPreview),
        withLatestFrom(
          // this.store.select(selectEditorFiles),
          this.store.select(selectEditorProjects),
        ),
        switchMap(([{ projectID }, projects]) => {
          if (projects[IdToIRI(projectID)]) {
            return [];
          }
          return [
            loadProjectPreviewRequest({ projectID }),
            setProjectPreviewIsLoading({ value: true }),
          ];
        }),
      ),
    );

    this.loadProjectPreviewRequest$ = createEffect(() =>
      this.actions$.pipe(
        ofType(loadProjectPreviewRequest),
        switchMap(({ projectID }) => {
          return this.editorService.loadProjectOverview(projectID);
        }),
        switchMap(({ project, files }) => [
          setProjectInput({ project, files }),
          setProjectPreviewIsLoading({ value: false }),
        ]),
      ),
    );

    this.setRelationships$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setRelationships),
        switchMap(({ IRI, relationships }, IRIs) => {
          // -- //

          return [];
        }),
      ),
    );

    this.setAnimationConfig$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setAnimationConfig),
        withLatestFrom(this.store.select(selectedShapes)),
        switchMap(
          ([
            { IRI, baseType, animationType, float, scale, by, mode },
            IRIs,
          ]) => {
            // -- //
            return [
              setAnimationConfigBase({
                baseType,
                animationType,
                float,
                scale,
                IRIs: [IRI],
                by,
                mode,
              }),
              setResourcePatch({
                IRIs: [IRI],
                field: 'literals',
                key: 'descriptor',
              }),
              setChangeTookPlace(),
            ];
          },
        ),
      ),
    );

    this.openCanvasBgColorEdit$ = createEffect(() =>
      this.actions$.pipe(
        ofType(openCanvasBgColorEdit),
        withLatestFrom(this.store.select(selectCurrentFileIRI)),
        switchMap(([, IRI]) => {
          return [
            setSelectedShapes({
              shapes: {
                [IRI]: true,
              },
            }),
            toggleEditorState({
              key: 'panelOpened',
              value: 'fill',
              force: true,
            }),
          ];
        }),
      ),
    );

    this.setMainAnimationFrame$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setMainAnimationFrame),
        withLatestFrom(this.store.select(selectCurrentFileIRI)),
        switchMap(([{ frame }, IRI]) => {
          console.log('setMainAnimationFrame$', { frame, IRI });
          return [
            // setChangeTookPlace(),
            // setResourcePatch({ IRI, field: 'literals', key: 'descriptor' }),
            setDescriptorValue({
              IRI,
              key: 'animationFrame',
              value: frame,
              direct: true,
            }),
          ];
        }),
      ),
    );

    this.removeAnimationItem$ = createEffect(() =>
      this.actions$.pipe(
        ofType(removeAnimationItemAction),
        switchMap(({ key, IRI }) => {
          return [
            setChangeTookPlace(),
            setResourcePatch({ IRI, field: 'literals', key: 'descriptor' }),
            removeAnimationItemBaseAction({ key, IRI }),
          ];
        }),
      ),
    );

    this.generateImage$ = createEffect(() =>
      this.actions$.pipe(
        ofType(generateImage),
        switchMap(({ prompt, style, imageFile }) => {
          const formData = new FormData();
          formData.append('prompt', prompt);
          formData.append('style', style);
          formData.append('image', imageFile); // imageFile is a File object from input or canvas blob
          return this.editorService.generateImage(formData);
        }),
        map(imageBlob => generateImageSuccess({ imageBlob })),
        // catchError((error) => of(generateImageFailure({ error })))
      ),
    );
    /***************  REQUEST RELATED  ****************/

    this.addFileToStore$ = createEffect(() =>
      this.actions$.pipe(
        ofType(addFileToStore),
        switchMap(({ file, shapes }) => {
          const canvasShapes = Object.keys(
            (file.relationships?.shapes as Record<string, boolean>) || [],
          ).map(shapeIRI => shapes[shapeIRI]);
          return [
            setCanvasShapes({ shapes: canvasShapes }),
            // initAnimationsOfFile({ file, shapes: canvasShapes }),
            setFileLoading({ value: false }),
            setCurrentAnimationId({ id: null }),
          ];
        }),
      ),
    );

    this.saveResources$ = createEffect(() =>
      this.actions$.pipe(
        ofType(saveResourcesAction),
        withLatestFrom(
          this.store.select(selectCrudEvents),
          this.store.select(selectShapeTransforms),
          this.store.select(baseSvgAttributes),
          this.store.select(selectDescriptors),
          this.store.select(selectUpdatesByState),
          this.store.select(selectAnimationsByResource),
          this.store.select(selectShapes),
          this.store.select(selectFiles),
          this.store.select(selectAllProjects),
          this.store.select(selectResources),
        ),
        switchMap(
          //@ts-ignore
          ([
            _action,
            crudEvents,
            transforms,
            svgAttributes,
            descriptors,
            updatesByState,
            animationsByResource,
            shapes,
            files,
            projects,
            // resources,
          ]) => {
            // -- // -- //
            return [];

            const resources = {
              ...shapes,
              ...files,
            };
            // -- // -- // -- // -- //
            const posts = Object.entries(crudEvents.posts || {}).map(
              ([IRI, type]) => {
                if (!resources[IRI]) {
                  throw new Error(`Resource ${IRI} not found`);
                }
                return {
                  IRI,
                  type: resources[IRI].type,
                  literals: {
                    ...resources[IRI].literals,
                    descriptor: {
                      ...descriptors[IRI],
                      position: transforms[IRI],
                      svgAttributes: svgAttributes[IRI],
                      updatesByState: updatesByState[IRI],
                      animations: animationsByResource[IRI],
                    },
                  },
                  relationships: pick(resources[IRI].relationships || {}, [
                    'file',
                    'childOf',
                  ]),
                } as ResourceData;
              },
            );

            const patches = Object.entries(crudEvents.patches || {}).map(
              ([IRI, patchDescription]) => {
                const { literals, relationships } =
                  patchDescription as ResourcePatch;
                const { label, descriptor } = literals || {};
                return {
                  IRI,
                  type: resources[IRI].type,
                  literals: {
                    label: label ? resources[IRI].literals.label : undefined,
                    descriptor: descriptor
                      ? {
                          ...descriptors[IRI],
                          position: transforms[IRI],
                          svgAttributes: svgAttributes[IRI],
                          updatesByState: updatesByState[IRI],
                          animations: animationsByResource[IRI],
                        }
                      : undefined,
                  },
                  relationships: Object.keys(relationships || {}).reduce(
                    (obj, key) => ({
                      ...obj,
                      [key]: resources[IRI].relationships?.[key],
                    }),
                    {},
                  ),
                };
              },
            );

            const deletes = Object.entries(crudEvents.deletes || {}).map(
              ([IRI, type]) =>
                ({
                  IRI,
                  type,
                }) as ResourceData,
            );

            if (
              posts.length == 0 &&
              patches.length == 0 &&
              deletes.length == 0
            ) {
              return of({ type: '[Save Resources] No Changes' });
            }
            return this.editorService.saveResources({
              posts,
              patches,
              deletes,
            });
          },
        ),
        catchError(error => {
          console.error('saveResourceAction error', error.message);
          return of({ type: '[Save Resources] Error' });
        }),
      ),
    );

    /**************  SHAPE/FILE MANIPULATION  ****************/

    this.appendComment$ = createEffect(() =>
      this.actions$.pipe(
        ofType(appendComment),
        withLatestFrom(
          this.store.select(myUserId),
          this.store.select(selectedShapes),
          this.store.select(selectDescriptors),
        ),
        switchMap(([{ text, time }, user, selectedShapes, descriptors]) => {
          const shapeIRIs = Object.values(selectedShapes);
          if (shapeIRIs.length !== 1) return [];
          const IRI = shapeIRIs[0];
          const descriptor = descriptors[IRI] as CommentShapeDescriptor;
          const prevComments = Array.isArray(descriptor?.comments)
            ? descriptor.comments
            : [];
          const newComment: Comment = {
            user,
            text,
            time,
          };
          return [
            setDescriptorValue({
              IRI,
              key: 'comments' as AnimationKeys,
              value: [...prevComments, newComment],
              direct: true,
              noAnimation: true,
            }),
            clearCommentDraft({
              commentIRI: IRI,
            }),
          ];
        }),
      ),
    );

    this.setDescriptorValue$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setDescriptorValue),
        withLatestFrom(
          this.store.select(selectCurrentAnimationId),
          this.store.select(selectNoAnimationMode),
          this.store.select(selectedSelectedState),
          this.store.select(selectedShapes),
          this.store.select(selectCurrentlyAddedShapes),
        ),
        switchMap(
          ([
            action,
            currentAnimationId,
            noAnimationMode,
            selectedState,
            selectedShapes,
            currentlyAddedShapes,
          ]): Action[] => {
            const { key, innerKey, value, IRI, direct, noAnimation } = action;
            const shapesToUpdate = IRI ? [IRI] : selectedShapes;

            /* console.log('-------- set-descriptor-value -----------', {
              key,
              innerKey,
              value,
              IRI,
              direct,
              noAnimation,
              currentAnimationId,
            }); */
            return [
              setBulkUpdateItem({
                items: shapesToUpdate.map(IRI => ({
                  IRI,
                  key,
                  value,
                  innerKey,
                  base: !currentAnimationId || currentlyAddedShapes[IRI],
                })),
              }),
              setResourcePatch({
                IRIs: shapesToUpdate,
                // resourceType: 'nw:Shape',
                field: 'literals',
                key: 'descriptor',
              }),
              setChangeTookPlace(),
              currentAnimationId &&
                setAnimationItemAction({
                  IRIs: shapesToUpdate.filter(
                    IRI => !currentlyAddedShapes[IRI],
                  ),
                  animationId: currentAnimationId,
                  key,
                  innerKey,
                  value,
                }),
              direct && bulkUpdateFinish(),
            ].filter(v => !!v);
          },
        ),
        catchError(e => {
          console.error('----- setDescValue ---', e.message);
          return of(e.message);
        }),
      ),
    );

    // this.setDescriptorValue$ = createEffect(() =>
    //   this.actions$.pipe(
    //     ofType(setDescriptorValue),
    //     withLatestFrom(
    //       this.store.select(selectCurrentAnimationId),
    //       this.store.select(selectNoAnimationMode),
    //       this.store.select(selectedSelectedState),
    //       this.store.select(selectedShapes),
    //       this.store.select(selectCurrentlyAddedShapes),
    //     ),
    //     switchMap(
    //       ([
    //         action,
    //         currentAnimationId,
    //         noAnimationMode,
    //         selectedState,
    //         selectedShapes,
    //         currentlyAddedShapes,
    //       ]): Action[] => {
    //         const { key, innerKey, value, IRI, direct, noAnimation } = action;
    //         const shapesToUpdate = IRI ? [IRI] : selectedShapes;
    //         /*
    //         console.log('--------- setDescriptorValue -----', {
    //           currentAnimationId,
    //           noAnimationMode,
    //           selectedState,
    //           selectedShapes,
    //         }); */
    //         // console.log({ currentlyAddedShapes });

    //         if (selectedState) {
    //           switch (key) {
    //             case 'translate':
    //               return [
    //                 setAttributeByState({
    //                   IRIs: shapesToUpdate,
    //                   state: selectedState,
    //                   key: 'position',
    //                   innerKey,
    //                   value,
    //                 }),
    //                 direct && bulkUpdateFinish(),
    //                 setChangeTookPlace(),
    //               ].filter(v => !!v);
    //             case 'scale':
    //               return [
    //                 setAttributeByState({
    //                   IRIs: shapesToUpdate,
    //                   state: selectedState,
    //                   keys: ['position', 'scale', innerKey as string],
    //                   value,
    //                 }),
    //                 direct && bulkUpdateFinish(),
    //                 setChangeTookPlace(),
    //               ].filter(v => !!v);
    //           }
    //         } else if (currentAnimationId && !noAnimationMode && !noAnimation) {
    //           // -- // -- // -- // -- // -- // -- // -- //
    //           // console.log('-- setAnimationItem --', action);
    //           let baseActions = [];
    //           const currentlyAddedAndSelectedShapes = shapesToUpdate.filter(
    //             IRI => currentlyAddedShapes[IRI],
    //           );
    //           if (currentlyAddedAndSelectedShapes.length) {
    //             baseActions = [
    //               setBulkUpdateItem({
    //                 items: currentlyAddedAndSelectedShapes.map(IRI => ({
    //                   IRI,
    //                   key,
    //                   value,
    //                   innerKey,
    //                   base: true,
    //                 })),
    //               }),
    //               setResourcePatch({
    //                 IRIs: currentlyAddedAndSelectedShapes,
    //                 resourceType: 'nw:Shape',
    //                 field: 'literals',
    //                 key: 'descriptor',
    //               }),
    //               setChangeTookPlace(),
    //               direct && bulkUpdateFinish(),
    //             ].filter(v => !!v);
    //           }
    //           // console.log('base-actions', baseActions);
    //           /* console.log(
    //             'set-animation-sub-value',
    //             shapesToUpdate.filter(IRI => !currentlyAddedShapes[IRI]),
    //           ); */
    //           return [
    //             ...baseActions,
    //             innerKey
    //               ? setAnimationSubValue({
    //                   IRIs: shapesToUpdate.filter(
    //                     IRI => !currentlyAddedShapes[IRI],
    //                   ),
    //                   animationKey: key,
    //                   innerKey,
    //                   value,
    //                 })
    //               : addAnimationItemAction({
    //                   IRIs: shapesToUpdate.filter(
    //                     IRI => !currentlyAddedShapes[IRI],
    //                   ),
    //                   item: {
    //                     key,
    //                     value: value as AnimationValue,
    //                   },
    //                 }),
    //             direct && bulkUpdateFinish(),
    //             setChangeTookPlace(),
    //           ].filter(v => !!v);
    //         } else {
    //           return [
    //             setBulkUpdateItem({
    //               items: shapesToUpdate.map(IRI => ({
    //                 IRI,
    //                 key,
    //                 value,
    //                 innerKey,
    //                 base: true,
    //               })),
    //             }),
    //             setResourcePatch({
    //               IRIs: shapesToUpdate,
    //               resourceType: 'nw:Shape',
    //               field: 'literals',
    //               key: 'descriptor',
    //             }),
    //             setChangeTookPlace(),
    //             direct && bulkUpdateFinish(),
    //           ].filter(v => !!v);
    //         }
    //       },
    //     ),
    //     catchError(e => {
    //       return of(e.message);
    //     }),
    //   ),
    // );

    this.incrementRadius$ = createEffect(() =>
      this.actions$.pipe(
        ofType(incrementRadius),
        withLatestFrom(
          this.store.select(selectedShapeList),
          this.store.select(selectDescriptors),
          this.store.select(currentDescriptors),
        ),
        switchMap(
          ([
            { increment },
            selectedShapes,
            descriptors,
            currentDescriptors,
          ]) => {
            return selectedShapes
              .filter(
                shapeIRI => descriptors[shapeIRI].type == 'rectangle-shape',
              )
              .map(shapeIRI => {
                const r =
                  (currentDescriptors[shapeIRI] as RectangleShapeDescriptor)
                    .r || 0;
                return setDescriptorValue({
                  IRI: shapeIRI,
                  key: 'r',
                  value: Math.max(0, r + increment),
                });
              });
          },
        ),
      ),
    );

    this.incrementStrokeWidth$ = createEffect(() =>
      this.actions$.pipe(
        ofType(incrementStrokeWidth),
        withLatestFrom(
          this.store.select(selectedShapeList),
          this.store.select(currentDescriptors),
          this.store.select(currentSVGAttributes),
        ),
        switchMap(
          ([{ increment }, selectedShapes, descriptors, svgAttributes]) => {
            console.log('incrementStrokeWidth', increment);
            return selectedShapes
              .filter(
                shapeIRI => descriptors[shapeIRI].type != 'imported-shape',
              )
              .map(shapeIRI => {
                const currentWidth =
                  (svgAttributes[shapeIRI] as SVGAttributes).stroke?.width || 1;

                return setDescriptorValue({
                  IRI: shapeIRI,
                  key: 'stroke',
                  innerKey: 'width',
                  value: currentWidth + increment,
                });
              });
          },
        ),
      ),
    );

    this.newShape$ = createEffect(() =>
      this.actions$.pipe(
        ofType(addNewShapeAction),
        // withLatestFrom(this.store.select(selectCurrentFileIRI)),
        switchMap(({ data }) => {
          return [
            setResourcePost({
              IRI: data.IRI,
              resourceType: 'nw:Shape',
            }),
            // addShapeToFile({
            //   shapeIRI: data.IRI,
            //   fileIRI,
            // }),
            addShapeToStore({ data }),
            setChangeTookPlace(),
          ];
        }),
        catchError(error => {
          return of(error.message);
        }),
      ),
    );

    this.addShapeToStore$ = createEffect(() =>
      this.actions$.pipe(
        ofType(addShapeToStore),
        switchMap(({ data }) => {
          const { IRI, literals, relationships } = data;

          const { descriptor } = literals;

          function getScale(descriptor: GeneralShapeDescriptor) {
            switch (descriptor.type) {
              case 'rectangle-shape':
                const { width, height } =
                  descriptor as RectangleShapeDescriptor;
                return { x: width, y: height };
              case 'circle-shape':
                const { rx, ry } = descriptor as CircleShapeDescriptor;
                return { x: 2 * rx, y: 2 * ry };
              case 'imported-shape':
                return descriptor.position.scale;
            }
            return;
          }

          // -- // -- //
          return [
            setShape({ shape: data }),
            // setRelationship({
            //   subject: IRI,
            //   relationship: 'file',
            //   object: relationships.file as string,
            // }),
            // relationships.childOf &&
            //   setRelationship({
            //     subject: IRI,
            //     relationship: 'childOf',
            //     object: relationships.childOf as string,
            //   }),
            setOriginalDescriptor({
              IRI,
              descriptor,
            }),
            setOriginalSVGAttributes({
              IRI: IRI,
              svgAttributes: descriptor.svgAttributes,
            }),
            setShapeOriginalTransform({
              IRI,
              transform: {
                translate:
                  descriptor.position?.x && descriptor.position?.y
                    ? (_pick(descriptor.position, 'x', 'y') as Translate)
                    : descriptor.position?.translate,
                scale: getScale(descriptor),
              },
            }),
            ...Object.entries(relationships || {}).map(
              ([relationship, object]) =>
                _setRelationshipBase({
                  subject: IRI,
                  relationship,
                  object: object as string,
                  save: true,
                }),
            ),
            setChangeTookPlace(),
            // descriptor._animationsByKey &&
            //   addAnimationsOfShapesToStoreBase({
            //     animations: [
            //       {
            //         IRI,
            //         animationsById: descriptor._animationsByKey,
            //       },
            //     ],
            //   }),
          ].filter(val => !!val);
        }),
      ),
    );

    this.deleteShape$ = createEffect(() =>
      this.actions$.pipe(
        ofType(deleteShapeAction),
        withLatestFrom(this.store.select(selectCurrentFileID)),
        switchMap(([{ IRI }, fildeID]) => {
          console.log('deleteShapeAction', IRI);
          return [
            setResourceDelete({
              resourceType: 'nw:Shape',
              IRI,
            }),
            // removeRelationshipBase({
            //   fileIRI: IdToIRI(fildeID),
            //   // subjectIRI: IRI,
            //   relationship: 'shapes',
            //   objectIRI: IRI,
            // }),
            setChangeTookPlace(),
            deselectShape({ IRI }),
          ];
        }),
        catchError(e => {
          // console.log('------ deleteShapeError ------', e);
          return [deleteShapeFailure()];
          // return of('abc');
        }),
      ),
    );

    // -- // -- //
    this.setRelationship$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setRelationship),
        withLatestFrom(
          this.store.select(selectShapes),
          this.store.select(selectFiles),
        ),
        switchMap(
          ([{ subject, relationship, object, objects }, shapes, files]) => {
            objects = objects ? objects : [object];
            let inverse: string;
            console.log('--- set-relationships ---', {
              subject,
              relationship,
              object,
              objects,
            });
            switch (relationship) {
              case 'childOf':
                return [
                  setRelationshipBase({
                    subject,
                    subjectType: shapes[subject].type ? 'nw:Shape' : 'nw:File',
                    relationship,
                    object,
                  }),

                  // setRelationshipBase({
                  //   subject: shapeIRI,
                  //   subjectType: 'nw:Shape',
                  //   relationship,
                  //   object,
                  // }),
                  setResourcePatch({
                    IRI: object,
                    field: 'relationships',
                    key: relationship,
                  }),
                ];

              case 'file':
                const shapeIRI = subject;
                const fileIRI = object;
                const currentFileOfShape = shapes[shapeIRI].relationships
                  .file as string;
                return [
                  setRelationshipBase({
                    subject: shapeIRI,
                    subjectType: 'nw:Shape',
                    relationship,
                    object: fileIRI,
                  }),
                  setResourcePatch({
                    IRI: shapeIRI,
                    field: 'relationships',
                    key: relationship,
                  }),
                  setRelationshipBase({
                    subject: fileIRI,
                    subjectType: 'nw:File',
                    relationship: 'shapes',
                    object: shapeIRI,
                    oneToMany: true,
                  }),
                ];
              case 'maskedBy':
                return objects.reduce(
                  (array, object) => [
                    ...array,
                    setRelationshipBase({
                      subject,
                      subjectType: 'nw:Shape',
                      relationship,
                      object,
                    }),
                    // saveChangedShapes({ IRI: subject }),
                    setResourcePatch({
                      IRI: subject,
                      field: 'relationships',
                      key: relationship,
                      // resourceType: 'nw:Shape',
                    }),
                    setRelationshipBase({
                      subject: object,
                      subjectType: 'nw:Shape',
                      relationship: 'maskChildren',
                      object: subject,
                    }),
                  ],
                  [],
                );
              case 'isInGroup':
                return [
                  setRelationshipBase({
                    subject,
                    subjectType: 'nw:Shape',
                    relationship,
                    object,
                  }),
                  setRelationshipBase({
                    subject: object,
                    subjectType: 'nw:File',
                    relationship: 'groupChildren',
                    object: subject,
                    oneToMany: true,
                  }),
                ];
              case 'layer':
                inverse = 'layerChildren';
                break;
            }
            return objects.reduce(
              (array, object) => [
                ...array,
                setRelationshipBase({
                  subject,
                  subjectType: 'nw:Shape',
                  relationship,
                  object,
                }),
                saveChangedShapes({ IRI: subject }),
                setRelationshipBase({
                  subject: object,
                  subjectType: 'nw:Shape',
                  relationship: 'layerChildren',
                  object: subject,
                }),
              ],
              [],
            );
          },
        ),
      ),
    );

    this.createNewComponent$ = createEffect(() =>
      this.actions$.pipe(
        ofType(createNewComponentAction),
        withLatestFrom(
          this.store.select(_selectedShapes),
          this.store.select(selectCurrentFileID),
        ),
        switchMap(([{ IRI, label, descriptor }, shapes, currentFileID]) => {
          // -- // -- // -- // -- // -- //
          return [
            addFile({
              file: {
                IRI,
                literals: {
                  label,
                  descriptor,
                },
                relationships: {
                  shapes,
                  componentOf: IRIToId(currentFileID),
                },
              },
            }),
            setResourcePost({
              IRI,
              resourceType: 'nw:File',
            }),
            ...Object.keys(shapes).map(shapeIRI =>
              setRelationship({
                subject: shapeIRI,
                relationship: 'file',
                object: IRI,
              }),
            ),
          ];
        }),
        catchError(error => {
          console.error('createNewComponentError', error.message);
          return of(error.message);
        }),
      ),
    );

    /**************  CANVAS STATE  ****************/

    // this.moveBack$ = createEffect(() =>
    //   this.actions$.pipe(
    //     ofType(moveBack),
    //     withLatestFrom(this.store.select(selectVisitStack)),
    //     switchMap(([, visitStack]) => {
    //       return [
    //         popVisitStack(),
    //         openFile({ ID: visitStack[visitStack.length - 1] }),
    //       ];
    //     }),
    //   ),
    // );

    this.openFile$ = createEffect(() =>
      this.actions$.pipe(
        ofType(openFile),
        withLatestFrom(
          this.store.select(selectFiles),
          this.store.select(selectShapes),
        ),
        switchMap(([{ IRI, importedShape }, files, shapes]) => {
          if (files[IRI]?.relationships?.shapes) {
            if (importedShape) {
              return [];
            }
            // This action sets the details of the current file into the store, indirectly to the animation service.
            return [
              addFileToStore({
                file: cloneDeep(files[IRI]),
                shapes,
              }),
            ];
          }
          return [
            setFileLoading({ value: true }),
            setCurrentFileIRI({
              IRI,
            }),
            loadFileAction({
              ID: IRIToId(IRI),
              mode: importedShape ? 'imported-shape' : 'main-file',
            }),
          ];
        }),
      ),
    );

    this.setResourceLabel$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setResourceLabel),
        switchMap(({ IRI, label }) => [
          setChangeTookPlace(),
          setResourcePatch({
            IRI,
            field: 'literals',
            key: 'label',
          }),
          setResourceLabelBase({ IRI, label }),
        ]),
      ),
    );

    // this.setStoryBoardItem$ = createEffect(() =>
    //   this.actions$.pipe(
    //     ofType(setStoryBoardItem),
    //     withLatestFrom(
    //       this.store.select(selectSelectedStoryBoardItemID),
    //       this.store.select(selectCurrentFile),
    //     ),
    //     map(([{ item }, selectedStoryBoardItem, currentFile]) => {
    //       const { literals } = currentFile;

    //       const storyBoard = literals.descriptor.storyBoard || {
    //         items: [
    //           {
    //             id: 1,
    //             notes: [],
    //           },
    //         ],
    //       };
    //       const index = storyBoard.items.findIndex(
    //         ({ id }) => id == selectedStoryBoardItem,
    //       );

    //       // -- // -- //
    //       const updatedItems = [...storyBoard.items]; // Create a new array
    //       updatedItems[index] = item; // Modify the copy

    //       return setFileDescriptorItem({
    //         key: 'storyBoard',
    //         value: {
    //           items: updatedItems,
    //         },
    //       });
    //     }),
    //   ),
    // );
    // this.resetBaseState$ = createEffect(() =>
    //   this.actions$.pipe(
    //     ofType(resetBaseState),
    //     withLatestFrom(
    //       this.store.select(baseShapeTransforms),
    //       this.store.select(baseSvgAttributes),
    //     ),
    //     switchMap(([, shapeTransforms]) => {
    //       // TODO - implement for other attributes like fill, stroke
    //       // console.log('resetBaseState', { shapeTransforms });
    //       const transforms = Object.entries(shapeTransforms)
    //         .filter(([shapeIRI]) => !shapeIRI.includes('_'))
    //         .map(([shapeIRI, transform]) =>
    //           setBaseShapeTransform({ IRI: shapeIRI, transform }),
    //         );
    //       return [...transforms, setCurrentAnimationIdBase({ id: undefined })];
    //     }),
    //   ),
    // );
  }
}
